<template>
  <main class="report-directions-revenue">
    <header class="report-directions-revenue__header">
      <h1 class="report-directions-revenue__title">Выручка</h1>
      <form
        class="report-directions-revenue__units"
        v-on:submit.prevent="onSubmit"
      >
        <div class="report-directions-revenue__unit">
          <div
            class="report-directions-revenue__group"
            v-if="hasFetchedOptions"
          >
            <date-range-with-presets
              v-bind:presets="periodPresets"
              custom-color-date="black"
              v-model="periodDates"
              v-on:input-preset="onChangePeriodType" />
            <the-select
              title="Тип отчета"
              v-model="selectedBase"
              v-bind:options="bases"
              class="report-directions-revenue__input"
            />
          </div>
        </div>
        <div class="report-directions-revenue__unit">
          <h2>Параметры фильтрации</h2>
          <div class="report-directions-revenue__group">
            <multiple-autocomplete-input
              class="report-directions-revenue__group__autocomplete"
              label="Категории услуг"
              removal-per-chip
              v-bind:items="categories"
              v-model="selectedCategories" />
            <multiple-autocomplete-input
              class="report-directions-revenue__group__autocomplete"
              label="Сотрудники"
              removal-per-chip
              v-bind:items="employees"
              v-model="selectedEmployees" />
            <multiple-autocomplete-input
              class="report-directions-revenue__group__autocomplete"
              label="Пациенты"
              removal-per-chip
              v-bind:items="patients"
              v-model="selectedPatients"
            >
              <template v-slot:prepend-item>
                <v-list-item
                  ripple
                  v-on:mousedown.prevent
                  v-on:click="onClickShowArchivePatients"
                >
                  <v-list-item-action>
                    <v-icon :color="showArchivePatients ? 'indigo darken-4' : ''">
                      {{ selectArchiveOptionIcon }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      Показать архивных
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </multiple-autocomplete-input>
            <multiple-autocomplete-input
              class="report-directions-revenue__group__autocomplete"
              label="Кассы"
              removal-per-chip
              v-bind:items="cashboxes"
              v-model="selectedCashboxes" />
          </div>
        </div>
        <div class="report-directions-revenue__unit">
          <h2>Группировка по уровням</h2>
          <level-grouping
            class="report-directions-revenue__group"
            v-model="selectedLevels"
            v-bind:levels="levels" />
        </div>
        <button
          class="report-directions-revenue__submit"
          type="submit">Сформировать</button>
        <div class="report-directions-revenue__print">
          <button
            type="button"
            v-on:click="fetchReportSpreadsheet"
            class="report-directions-revenue__button"
          >EXCEL</button>
          <input-checkbox
            v-model="fullReport"
            title="Детальный отчет"
          />
        </div>
      </form>
    </header>
    <section class="report-directions-revenue__body">
      <table
        class="report-directions-revenue__table"
        v-if="id">
        <thead class="report-directions-revenue__table__header">
          <tr>
            <th
              class="report-directions-revenue__table__header__cell"
              v-for="{ sorts, title } in columns"
              v-bind:key="title"
            >
              <sort-by-column
                class="report-directions-revenue__table__sort"
                v-bind:sorts="sorts"
                v-bind:title="title"
                v-bind:value="selectedSort"
                v-if="sorts.length"
                v-on:input="onInputSort" />
              <template v-else>{{ title }}</template>
            </th>
          </tr>
        </thead>
        <tbody class="report-directions-revenue__table__body">
          <template
            v-for="{
              depth,
              fields,
              key,
              tags,
            } in bodyLines">
            <tr
              class="report-directions-revenue__table__body__line"
              v-bind:key="key">
              <td
                class="report-directions-revenue__table__body__cell"
                v-for="(title, fieldIndex) in fields"
                v-bind:key="`${key}:${fieldIndex}`">
                <label
                  class="report-directions-revenue__table__expansion"
                  v-bind:style="{ 'padding-left': `${depth}rem` }"
                  v-if="fieldIndex === 0 && tags.includes('parent')">
                  <input
                    type="checkbox"
                    v-bind:value="key"
                    v-model="expandedLines">
                  <span />
                </label>
                {{ title }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <em v-else-if="isFetchingReport">Идет построение отчета...</em>
    </section>
  </main>
</template>
<script>
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import { convertToInputDate } from '@/utils/timeFormat';
import TheSelect from '@/components/TheSelect.vue';
import notifierMixin from '@/mixins/notifierMixin';
import InputCheckbox from '@/components/InputCheckbox.vue';
import { getISODateStringByDate } from '@/mixins/dateMixin';

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

/**
 * Часть названия отчета: Отчет по выручке
 *
 * @type {string}
 */
const PART_OF_REPORT_REPORT_BY_REVENUE = 'Отчет по выручке';

/**
 * Часть названия отчета: (детальный)
 *
 * @type {string}
 */
const PART_OF_REPORT_DETAILING = '(детальный)';

/**
 * @description Уровневая группировка.
 * @type {Vue}
 */
export const LevelGrouping = {
  computed: {
    /**
     * @description Селекторы выбора уровня группировки.
     * @returns {any[]}
     */
    selectors() {
      const resolveOptions = (levelOptions) => [{
        disabled: false,
        title: 'Нет',
        value: '',
      }].concat(levelOptions);
      return Array.from({ length: this.levels.length }, (_, index) => {
        const options = resolveOptions(this.levels.map((item) => {
          const disabled = this.value.length >= index
            ? this.value.includes(item.value)
            : true;
          return {
            disabled,
            title: item.title,
            value: item.value,
          };
        }));
        return {
          key: `level-grouping:${index}`,
          on: { input: (value) => this.onSelectLevel(value, index) },
          options,
          title: `Уровень ${index + 1}`,
          value: this.value[index],
        };
      });
    },
  },
  props: {
    levels: {
      required: true,
      type: Array,
    },
    value: {
      required: true,
      type: Array,
    },
  },
  methods: {
    onSelectLevel(value, index) {
      if (this.value.includes(value)) {
        return;
      }
      const updatedValues = this.value.slice(0, index);
      if (value) {
        const rightLevels = this.value.slice(index + 1);
        updatedValues.push(value, ...rightLevels);
      }
      this.$emit('input', updatedValues);
    },
  },
  render(createElement) {
    return createElement('div', this.selectors.map(
      ({
        key,
        on,
        options,
        title,
        value,
      }) => createElement(TheSelect, {
        attrs: {
          options,
          title,
          value,
        },
        key,
        on,
      }),
    ));
  },
};

/**
 * @description Сортируемая колонка.
 * @type {Vue}
 */
const SortByColumn = {
  computed: {
    headTitle() {
      const sort = this.sortedItems[0];
      return sort ? sort.title : this.title;
    },
    sortedItems() {
      const items = [
        ...this.sorts.map(({ id, tags, title }) => ({
          checked: tags.includes('actual'),
          tags,
          title,
          value: id,
        })),
      ];
      let slicedPosition = 0;
      const checkedIndex = items.findIndex(({ checked }) => checked);
      if (checkedIndex !== -1) {
        slicedPosition = checkedIndex + 1;
      }
      if (slicedPosition === items.length) {
        return items;
      }
      return [
        ...items.slice(slicedPosition),
        ...items.slice(0, slicedPosition),
      ];
    },
  },
  props: {
    sorts: {
      required: true,
      type: Array,
    },
    title: {
      required: true,
      type: String,
    },
    value: {
      required: true,
      type: String,
    },
  },
  render(createElement) {
    const name = `report-column:${this.title}`;
    const inputElements = this.sortedItems.map(({
      checked,
      tags,
      title,
      value,
    }) => createElement('input', {
      attrs: {
        checked,
        ...(tags.length ? { class: tags.join(' ') } : {}),
        name,
        title,
        type: 'radio',
      },
      domProps: { checked },
      key: `${name}:sort:${value}`,
      on: { input: () => { this.$emit('input', value); } },
    }));
    return createElement('label', { attrs: { title: this.headTitle } }, [
      createElement('span', this.title).children[0],
      ...inputElements,
    ]);
  },
};

/**
 * @description Раздел отчета о выручке по направлениям.
 * @type {Vue}
 */
export default {
  components: {
    InputCheckbox,
    DateRangeWithPresets,
    LevelGrouping,
    MultipleAutocompleteInput,
    SortByColumn,
    TheSelect,
  },
  computed: {
    /**
     * @description Иконка элемента "показать архивных"
     * @return {string}
     */
    selectArchiveOptionIcon() {
      if (this.showArchivePatients) {
        return 'mdi-close-box';
      }
      if (this.showArchivePatients) {
        return 'mdi-minus-box';
      }
      return 'mdi-checkbox-blank-outline';
    },
    bodyLines() {
      const render = (path) => ({ children, fields, tags }, index) => {
        const depth = path.length;
        const key = [...path, index].join(':');
        const line = {
          depth,
          fields,
          key,
          tags,
        };
        if (tags.includes('parent') && this.expandedLines.includes(key)) {
          return [line, ...children.flatMap(render([...path, index]))];
        }
        return [line];
      };
      return this.lines.flatMap(render(['line']));
    },
    /**
     * @description Предварительные установки периодов.
     * @returns {{toValue: function(): [Date,Date], id: string, label: string, title: string}[]}
     */
    periodPresets() {
      return this.periodTypes.map(({ periodDates, title, value }) => ({
        id: value,
        label: title,
        title,
        toValue: () => periodDates,
      }));
    },
    /**
     * @description Выбранные даты периода.
     * @return {string}
     */
    selectedPeriod() {
      return this.periodDates.map(convertToInputDate).sort().join(',');
    },
  },
  created() {
    this
      .fetchOptions()
      .then(() => {
        this
          .bases
          .slice(0, 1)
          .forEach(({ value }) => { this.selectedBase = value; });
        this.selectedLevels = this
          .levels
          .slice(0, 2)
          .map(({ value }) => value);
        this
          .periodTypes
          .slice(0, 1)
          .forEach(({ periodDates, value }) => {
            this.periodDates = periodDates;
            this.selectedPeriodType = value;
          });
        this.fetchReport();
      })
      .finally(() => { this.$nextTick(() => { this.hasFetchedOptions = true; }); });
  },
  data() {
    return {
      /** Наименование филиала */
      branchName: '',
      bases: [],
      categories: [],
      columns: [],
      employees: [],
      expandedLines: [],
      hasFetchedOptions: false,
      id: null,
      isFetchingReport: false,
      levels: [],
      lines: [],
      patients: [],
      cashboxes: [],
      periodDates: [],
      periodTypes: [],
      selectedBase: '',
      selectedCategories: [],
      selectedEmployees: [],
      selectedLevels: [],
      selectedPatients: [],
      selectedCashboxes: [],
      selectedPeriodType: '',
      selectedSort: '',
      fullReport: false,
      showArchivePatients: false,
    };
  },
  name: 'ClinicPanelDirectionsRevenue',
  methods: {
    fetchReport() {
      this.isFetchingReport = true;
      this
        .$reportGateway
        .getDirectionsRevenueReport(
          this.selectedBase,
          this.selectedCategories,
          this.selectedEmployees,
          this.selectedLevels,
          this.selectedPatients,
          this.selectedPeriod,
          this.selectedPeriodType,
          this.selectedSort,
          this.selectedCashboxes,
        )
        .then((result) => {
          result.forEach(({ columns, id, lines }) => {
            this.columns = columns;
            this.expandedLines = [];
            this.id = id;
            this.lines = lines;
          });
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
        })
        .finally(() => { this.isFetchingReport = false; });
    },
    fetchReportSpreadsheet() {
      const reportName = this.getReportFileName();
      const onSuccess = (response) => {
        saveFile(response, reportName);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$reportGateway
        .getDirectionsRevenueReportBySheet(this.id, this.fullReport)
        .then((result) => result.fold(onFail, onSuccess));
    },
    fetchOptions() {
      return this
        .$reportGateway
        .getDirectionsRevenueReportOptions(this.showArchivePatients)
        .then((result) => {
          result.forEach((options) => {
            this.bases = options.bases;
            this.categories = options.categories;
            this.employees = options.employees;
            this.levels = options.levels;
            this.patients = options.patients;
            this.cashboxes = options.cashboxes;
            this.branchName = options.branch_name;
            this.periodTypes = options
              .period_types
              .map(({ from_to: [from, to], title, value }) => ({
                periodDates: [new Date(from), new Date(to)],
                title,
                value,
              }));
          });
        });
    },
    /**
     * @description Сформировать название отчета для файла.
     * @returns {string}
     */
    getReportFileName() {
      const parts = [];
      parts.push(PART_OF_REPORT_REPORT_BY_REVENUE);
      if (this.fullReport) {
        parts.push(PART_OF_REPORT_DETAILING);
      }
      if (this.branchName && this.branchName.trim() !== '') {
        parts.push(this.branchName.trim());
      }
      const [dateFrom, dateTo] = this.periodDates.map(getISODateStringByDate);
      parts.push(`${dateFrom} — ${dateTo}`);
      const fileName = parts
        .filter((part) => part && part.trim() !== '')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      return `${fileName}.xls`;
    },
    /**
     * @description Обработать нажатие отображаения архивных пациентов.
     * @returns {void}
     */
    onClickShowArchivePatients() {
      this.showArchivePatients = !this.showArchivePatients;
      this.fetchOptions();
    },
    onChangePeriodType(type) {
      this.selectedPeriodType = type;
    },
    onInputSort(sort) {
      this.selectedSort = sort;
      this.fetchReport();
    },
    onSubmit() {
      this.id = null;
      this.fetchReport();
    },
  },
  mixins: [notifierMixin],
};
</script>
<style lang="scss">
@use "@/scss/buttons";
.report-directions-revenue {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  &__header {
    display: flex;
    flex-direction: column;
    gap: 20px;
    background: #F2F5F8;
    width: 100%;
    padding: 38px 20px 48px 23px;
  }
  &__units {
    background: #F2F5F8;
    max-width: 1283px;
  }
  &__unit {
    display: flex;
    flex-direction: column;
    gap: 25px;
    padding: 20px 0 30px;
    border-top: 1px solid rgba(194, 207, 224, 0.50);
    h2 {
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      font-family: "Lato", sans-serif;
    }
    &:first-child {
      border-top: none;
    }
    &:nth-child(2) {
      gap: 0px;
    }
  }
  &__group {
    display: flex;
    flex-flow: row wrap;
    align-items: flex-end;
    column-gap: 40px;
    row-gap: 20px;
    &__autocomplete {
      max-width: 348px;
    }
  }
  &__print {
    display: flex;
    flex-flow: row wrap;
    align-items: baseline;
    gap: 20px;
  }
  &__input {
    max-width: 270px;
    width: 100%;
  }
  &__submit {
    @include buttons.btn-blue(210px, 48px, 16px);
    margin: 20px 0 0 0;
  }
  &__button {
    @include buttons.btn-transparent(210px, 48px, 16px);
    margin: 20px 0 0 0;
    &:before {
      content: "";
      background-image: url("@/assets/img/exel.svg");
      background-repeat: no-repeat;
      background-size: contain;
      width: 16px;
      height: 16px;
      margin: 0 8px 0 0;
    }
  }
  &__body {
    margin: 85px 0 0 0;
    padding: 0 20px 100px 23px;
  }
  &__table {
    border-collapse: collapse;
    font-size: 14px;
    line-height: 16px;
    overflow: auto;
    width: 100%;
    &__header {
      border-bottom: 2px solid #0489E0;
      font-weight: 700;
      &__cell {
        padding: 10px 8px;
        text-align: left;
      }
    }
    &__body {
      &__cell {
        padding: 15px 8px;
        text-align: left;
        white-space: nowrap;
        &:first-child {
          white-space: pre-wrap;
        }
      }
      &__line {
        border-bottom: 1px solid #EAEFF3;
        &:first-child {
          text-transform: uppercase;
          letter-spacing: 0.14px;
        }
      }
    }
    &__expansion {
      position: relative;
      &>input {
        position: absolute;
        z-index: -1;
        opacity: 0;
        &:checked+span {
          transform: rotate(180deg);
          background-position: top;
        }
      }
      &>span {
        display: inline-flex;
        align-items: center;
        user-select: none;
        cursor: pointer;
        background-image: url("@/assets/img/checked/arrow-top-blue.svg");
        background-size: contain;
        background-position: bottom;
        width: 15px;
        height: 15px;
        margin: 0 17px 0 0;
      }
    }
    &__sort {
      cursor: pointer;
      &:after {
        background-image: url("@/assets/img/checked/checked-grey.svg");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: '';
        display: inline-block;
        height: 8px;
        margin: 0 0 0 2px;
        min-height: 8px;
        min-width: 8px;
        width: 8px;
      }
      & input {
        display: none;
      }
    }
  }
}
@media screen and (max-width: 800px) {
  .report-directions-revenue {
    &__body {
      overflow: scroll;
    }
    &__table {
      width: 800px;
    }
  }
}
@media screen and (max-width: 600px) {
  .report-directions-revenue {
    &__body {
      margin: 40px 0 0 0;
    }
    &__group {
      flex-direction: column;
      align-items: flex-start;
    }
    &__input {
      max-width: 100%;
    }
    &__group {
      .v-input {
        width: 100%;
      }
      label.select {
        width: 100%;
      }
    }
  }
}
@media screen and (max-width: 480px) {
  .report-directions-revenue {
    &__header {
      padding: 40px 15px;
    }
    &__body {
      padding: 22px 15px 50px;
    }
  }
}
</style>
